import { DiscoverUser, DiscoverFilters, DiscoverStats, DiscoverAction, SavedUser, DiscoverServiceResponse } from '../types/discover';
import { User } from '../hooks/useAuth';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class DiscoverService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Discover Service initialized:', {
        baseURL: this.baseURL,
        environment: process.env.NODE_ENV
      });
    }
  }

  // Get authentication token from localStorage
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  // Helper to map a raw backend user document (not a match wrapper) to DiscoverUser format.
  // Used by browsePublicUsers where there is no compatibility score.
  private mapRawUserToDiscoverUser(backendUser: any): DiscoverUser {
    const minRent = backendUser.housingInfo?.rentPreferences?.minRent;
    const maxRent = backendUser.housingInfo?.rentPreferences?.maxRent;
    const budgetString = minRent != null && maxRent != null
      ? `$${minRent}-${maxRent}`
      : '';

    return {
      id: String(backendUser._id || backendUser.id),
      user: {
        id: String(backendUser._id || backendUser.id),
        firstName: backendUser.personalInfo?.firstName || '',
        lastName: backendUser.personalInfo?.lastName || '',
        age: backendUser.personalInfo?.age || 0,
        profilePicture: backendUser.personalInfo?.profilePicture,
        sex: Array.isArray(backendUser.personalInfo?.sex)
          ? backendUser.personalInfo.sex[0]
          : backendUser.personalInfo?.sex || '',
        username: backendUser.account?.username || '',
        bio: backendUser.personalInfo?.bio,
        verified: backendUser.metadata?.verified || false,
        profileCompleteness: backendUser.metadata?.profileCompleteness || 0
      },
      preferences: {
        location: backendUser.housingInfo?.selectedLocations || [],
        budget: budgetString,
        moveInDate: backendUser.housingInfo?.moveInDate || '',
        housingSituation: backendUser.housingInfo?.housingSituation || '',
        occupation: backendUser.occupation || '',
        cleanliness: backendUser.lifestyle?.cleanliness || '',
        socialLevel: backendUser.lifestyle?.socialLevel || '',
        workSchedule: backendUser.lifestyle?.workSchedule || '',
        hasPets: Array.isArray(backendUser.lifestyle?.hasPets)
          ? backendUser.lifestyle.hasPets
          : backendUser.lifestyle?.hasPets ? [backendUser.lifestyle.hasPets] : [],
        okWithPets: Array.isArray(backendUser.lifestyle?.okWithPets)
          ? backendUser.lifestyle.okWithPets
          : backendUser.lifestyle?.okWithPets ? [backendUser.lifestyle.okWithPets] : [],
        languages: Array.isArray(backendUser.languages)
          ? backendUser.languages
          : backendUser.languages ? [backendUser.languages] : []
      },
      // No compatibility score for guests — components check isAuthenticated before rendering
      compatibility: { overall: 0, location: 0, budget: 0, lifestyle: 0, timeline: 0 },
      lastActive: backendUser.metadata?.lastLogin
        ? new Date(backendUser.metadata.lastLogin).toISOString()
        : new Date().toISOString(),
      isOnline: false,
      photos: backendUser.personalInfo?.photos || [],
      interests: backendUser.personalInfo?.interests || [],
      languages: Array.isArray(backendUser.languages) ? backendUser.languages : [],
      occupation: backendUser.occupation || '',
      education: backendUser.personalInfo?.education,
      hasLiked: false,
      hasPassed: false,
      hasSaved: false,
      hasReported: false
    };
  }

  // Helper to map backend user to DiscoverUser format
  private mapBackendUserToDiscoverUser(backendMatch: any, currentUser: User): DiscoverUser {
    const backendUser = backendMatch.user;
    const compatibilityScore = backendMatch.compatibilityScore || 0;
    
    // Map compatibility score to CompatibilityScore structure
    // Note: Backend returns overall score, we map it to all required fields
    const compatibility = {
      overall: compatibilityScore,
      location: compatibilityScore, // Use overall score as approximation
      budget: compatibilityScore,
      lifestyle: compatibilityScore,
      timeline: compatibilityScore
    };

    // Extract user info
    const userInfo = {
      id: String(backendUser._id || backendUser.id),
      firstName: backendUser.personalInfo?.firstName || '',
      lastName: backendUser.personalInfo?.lastName || '',
      age: backendUser.personalInfo?.age || 0,
      profilePicture: backendUser.personalInfo?.profilePicture,
      sex: Array.isArray(backendUser.personalInfo?.sex) 
        ? backendUser.personalInfo.sex[0] 
        : backendUser.personalInfo?.sex || '',
      username: backendUser.account?.username || '',
      bio: backendUser.personalInfo?.bio,
      verified: backendUser.metadata?.verified || false,
      profileCompleteness: backendUser.metadata?.profileCompleteness || 0
    };

    // Build budget string from rentPreferences (actual schema: housingInfo.rentPreferences.minRent/maxRent)
    const minRent = backendUser.housingInfo?.rentPreferences?.minRent;
    const maxRent = backendUser.housingInfo?.rentPreferences?.maxRent;
    const budgetString = minRent != null && maxRent != null
      ? `$${minRent}-${maxRent}`
      : '';

    // Extract preferences (MatchPreferences requires all fields)
    const preferences = {
      location: backendUser.housingInfo?.selectedLocations || [],
      budget: budgetString,
      moveInDate: backendUser.housingInfo?.moveInDate || '',
      housingSituation: backendUser.housingInfo?.housingSituation || '',
      occupation: backendUser.occupation || '',
      cleanliness: backendUser.lifestyle?.cleanliness || '',
      socialLevel: backendUser.lifestyle?.socialLevel || '',
      workSchedule: backendUser.lifestyle?.workSchedule || '',
      hasPets: Array.isArray(backendUser.lifestyle?.hasPets)
        ? backendUser.lifestyle.hasPets
        : backendUser.lifestyle?.hasPets
          ? [backendUser.lifestyle.hasPets]
          : [],
      okWithPets: Array.isArray(backendUser.lifestyle?.okWithPets)
        ? backendUser.lifestyle.okWithPets
        : backendUser.lifestyle?.okWithPets
          ? [backendUser.lifestyle.okWithPets]
          : [],
      languages: Array.isArray(backendUser.languages)
        ? backendUser.languages
        : backendUser.languages
          ? [backendUser.languages]
          : []
    };

    // Extract additional info
    const photos = backendUser.personalInfo?.photos || [];
    const interests = backendUser.personalInfo?.interests || [];
    const languages = preferences.languages; // Use from preferences
    const occupation = preferences.occupation; // Use from preferences
    const education = backendUser.personalInfo?.education;
    
    return {
      id: userInfo.id,
      user: userInfo,
      preferences,
      compatibility,
      lastActive: backendUser.metadata?.lastLogin 
        ? new Date(backendUser.metadata.lastLogin).toISOString()
        : new Date().toISOString(),
      isOnline: false, // Backend doesn't track online status yet
      photos,
      interests,
      languages,
      occupation,
      education,
      hasLiked: false,
      hasPassed: false,
      hasSaved: false,
      hasReported: false
    };
  }

  // Build query string from filters
  private buildQueryString(filters: DiscoverFilters, page: number, limit: number): string {
    const params = new URLSearchParams();
    
    // Pagination
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Filters
    // Exclude any borough that already has a neighborhoodIds refinement — sending both would cause
    // the backend to expand the borough into all its neighborhoods, overriding the specific filter.
    if (filters.locations && filters.locations.length > 0) {
      const neighborhoodIds = filters.neighborhoodIds || [];
      const locationsToSend = filters.locations.filter(
        borough => !neighborhoodIds.some(id => id.startsWith(borough + '-'))
      );
      if (locationsToSend.length > 0) {
        params.append('locations', locationsToSend.join(','));
      }
    }
    
    if (filters.gender) {
      params.append('gender', filters.gender);
    }
    
    if (filters.budget) {
      params.append('budget', filters.budget);
    }
    
    if (filters.cleanliness) {
      params.append('cleanliness', filters.cleanliness);
    }

    if (filters.socialLevel) {
      params.append('socialLevel', filters.socialLevel);
    }

    if (filters.workSchedule) {
      params.append('workSchedule', filters.workSchedule);
    }

    if (filters.moveInDate) {
      params.append('moveInDate', filters.moveInDate);
    }

    if (filters.neighborhoodIds && filters.neighborhoodIds.length > 0) {
      params.append('neighborhoodIds', filters.neighborhoodIds.join(','));
    }
    
    if (filters.hasPets !== undefined) {
      params.append('hasPets', filters.hasPets.toString());
    }
    
    if (filters.okWithPets && filters.okWithPets.length > 0) {
      params.append('okWithPets', filters.okWithPets.join(','));
    }
    
    if (filters.languages && filters.languages.length > 0) {
      params.append('languages', filters.languages.join(','));
    }
    
    if (filters.requireNeighborhoodOverlap !== undefined) {
      params.append('requireNeighborhoodOverlap', filters.requireNeighborhoodOverlap.toString());
    }
    
    return params.toString();
  }

  // Browse public users (no auth required) — used by guest discover path
  async browsePublicUsers(
    filters: DiscoverFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<DiscoverServiceResponse<DiscoverUser[]>> {
    try {
      const queryString = this.buildQueryString(filters, page, limit);
      const url = `${this.baseURL}/users/browse?${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
        // No Authorization header — this is a public endpoint
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`,
          errors: [errorData]
        };
      }

      const data = await response.json();

      if (data.success && data.data && data.data.users) {
        const discoverUsers: DiscoverUser[] = data.data.users.map((u: any) =>
          this.mapRawUserToDiscoverUser(u)
        );

        const pag = data.data.pagination || {};
        const totalUsers = pag.totalUsers || 0;
        const usersPerPage = pag.usersPerPage || limit;
        const currentPage = pag.currentPage || page;
        const totalPages = pag.totalPages || Math.ceil(totalUsers / usersPerPage);

        return {
          success: true,
          message: 'Users retrieved successfully',
          data: discoverUsers,
          pagination: {
            page: currentPage,
            limit: usersPerPage,
            total: totalUsers,
            totalPages,
            hasMore: pag.hasNext || false,
            hasPrev: pag.hasPrev || currentPage > 1
          }
        };
      }

      return {
        success: false,
        message: data.message || 'Failed to browse users',
        errors: [data]
      };
    } catch (error) {
      console.error('❌ Discover Service Error (browse):', error);
      return {
        success: false,
        message: (error as Error).message || 'Failed to browse users',
        errors: [error]
      };
    }
  }

  // Discover users with filters and pagination
  async discoverUsers(
    currentUser: User, 
    filters: DiscoverFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<DiscoverServiceResponse<DiscoverUser[]>> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login.',
          errors: ['No authentication token found']
        };
      }

      const queryString = this.buildQueryString(filters, page, limit);
      const url = `${this.baseURL}/users/${currentUser.id}/discover?${queryString}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`,
          errors: [errorData]
        };
      }

      const data = await response.json();
      
      if (data.success && data.data && data.data.matches) {
        // Map backend matches to DiscoverUser format
        const discoverUsers = data.data.matches.map((match: any) => 
          this.mapBackendUserToDiscoverUser(match, currentUser)
        );

        // Extract pagination info
        const pagination = data.data.pagination || {};
        const totalUsers = pagination.totalUsers || 0;
        const usersPerPage = pagination.usersPerPage || limit;
        const currentPage = pagination.currentPage || page;
        const totalPages = pagination.totalPages || Math.ceil(totalUsers / usersPerPage);
        
        return {
          success: true,
          message: 'Users discovered successfully',
          data: discoverUsers,
          pagination: {
            page: currentPage,
            limit: usersPerPage,
            total: totalUsers,
            totalPages: totalPages,
            hasMore: pagination.hasNext || false,
            hasPrev: pagination.hasPrev || currentPage > 1
          }
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to discover users',
          errors: [data]
        };
      }
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: (error as Error).message || 'Failed to discover users',
        errors: [error]
      };
    }
  }

  // Get user suggestions (for "People You May Like")
  async getSuggestions(
    currentUser: User,
    limit: number = 10
  ): Promise<DiscoverServiceResponse<DiscoverUser[]>> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login.',
          errors: ['No authentication token found']
        };
      }

      const url = `${this.baseURL}/users/${currentUser.id}/suggestions?page=1&limit=${limit}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`,
          errors: [errorData]
        };
      }

      const data = await response.json();

      if (data.success && data.data && data.data.matches) {
        return {
          success: true,
          message: 'Suggestions retrieved successfully',
          data: data.data.matches.map((match: any) =>
            this.mapBackendUserToDiscoverUser(match, currentUser)
          )
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to get suggestions',
          errors: [data]
        };
      }
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: 'Failed to get suggestions',
        errors: [error]
      };
    }
  }

  // Perform discovery action (like, pass, save, etc.)
  // TODO: Implement backend endpoint for actions
  async performAction(
    action: DiscoverAction
  ): Promise<DiscoverServiceResponse<any>> {
    try {
      // Note: Backend endpoint not yet implemented
      // For now, return success (client-side state will be updated)
      return {
        success: true,
        message: 'Action performed successfully',
        data: { action }
      };
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: 'Failed to perform action',
        errors: [error]
      };
    }
  }

  // Save user for later viewing
  // TODO: Implement backend endpoint for saving users
  async saveUser(userId: string): Promise<DiscoverServiceResponse<boolean>> {
    // Note: Backend endpoint not yet implemented
    // For now, return success (client-side state will be updated)
    return {
      success: true,
      message: 'User saved successfully',
      data: true
    };
  }

  // Unsave user
  // TODO: Implement backend endpoint for unsaving users
  async unsaveUser(userId: string): Promise<DiscoverServiceResponse<boolean>> {
    // Note: Backend endpoint not yet implemented
    // For now, return success (client-side state will be updated)
    return {
      success: true,
      message: 'User unsaved successfully',
      data: true
    };
  }

  // Get saved users
  async getSavedUsers(): Promise<DiscoverServiceResponse<SavedUser[]>> {
    try {
      // Mock saved users
      const savedUsers: SavedUser[] = [
        {
          id: 'saved-1',
          userId: 'discover-1',
          savedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Great compatibility, love her art interests'
        },
        {
          id: 'saved-2',
          userId: 'discover-3',
          savedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Medical student, very clean'
        }
      ];

      return {
        success: true,
        message: 'Saved users retrieved successfully',
        data: savedUsers
      };
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: 'Failed to get saved users',
        errors: [error]
      };
    }
  }

  // Report inappropriate user
  // TODO: Implement backend endpoint for reporting users
  async reportUser(
    userId: string, 
    reason: string, 
    details?: string
  ): Promise<DiscoverServiceResponse<boolean>> {
    // Note: Backend endpoint not yet implemented
    // For now, return success (client-side state will be updated)
    return {
      success: true,
      message: 'User reported successfully',
      data: true
    };
  }

  // Get discovery statistics
  async getDiscoverStats(): Promise<DiscoverServiceResponse<DiscoverStats>> {
    try {
      // For now, return empty stats since backend doesn't provide this endpoint yet
      // This can be enhanced later when backend adds stats endpoint
      const stats: DiscoverStats = {
        totalUsers: 0,
        filteredUsers: 0,
        newThisWeek: 0,
        verifiedUsers: 0,
        averageCompatibility: 0
      };
      
      return {
        success: true,
        message: 'Discovery statistics retrieved successfully',
        data: stats
      };
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: 'Failed to get discovery statistics',
        errors: [error]
      };
    }
  }

  // Send match invitation
  async sendMatchInvitation(
    targetUserId: string,
    message?: string
  ): Promise<DiscoverServiceResponse<any>> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login.',
          errors: ['No authentication token found']
        };
      }

      const url = `${this.baseURL}/match-requests`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toUserId: targetUserId,
          message: message || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`,
          errors: [errorData]
        };
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          message: 'Match invitation sent successfully',
          data: data.data || data
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to send match invitation',
          errors: [data]
        };
      }
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: (error as Error).message || 'Failed to send match invitation',
        errors: [error]
      };
    }
  }

  // Get match invitations (sent and received)
  async getMatchInvitations(): Promise<DiscoverServiceResponse<any[]>> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login.',
          errors: ['No authentication token found']
        };
      }

      const url = `${this.baseURL}/match-requests`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${response.status}`,
          errors: [errorData]
        };
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          message: 'Match invitations retrieved successfully',
          data: data.data || []
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to get match invitations',
          errors: [data]
        };
      }
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: (error as Error).message || 'Failed to get match invitations',
        errors: [error]
      };
    }
  }

  // Respond to match invitation
  async respondToInvitation(
    invitationId: string,
    response: 'accepted' | 'declined'
  ): Promise<DiscoverServiceResponse<any>> {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login.',
          errors: ['No authentication token found']
        };
      }

      const endpoint = response === 'accepted' ? 'accept' : 'decline';
      const url = `${this.baseURL}/match-requests/${invitationId}/${endpoint}`;
      
      const fetchResponse = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({ message: `HTTP ${fetchResponse.status}` }));
        return {
          success: false,
          message: errorData.message || `HTTP error! status: ${fetchResponse.status}`,
          errors: [errorData]
        };
      }

      const data = await fetchResponse.json();
      
      if (data.success) {
        return {
          success: true,
          message: `Invitation ${response} successfully`,
          data: data.data || { invitationId, response }
        };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to respond to invitation',
          errors: [data]
        };
      }
    } catch (error) {
      console.error('❌ Discover Service Error:', error);
      return {
        success: false,
        message: (error as Error).message || 'Failed to respond to invitation',
        errors: [error]
      };
    }
  }
}

// Create singleton instance
const discoverService = new DiscoverService();

// Export service methods
export const discoverAPI = {
  browsePublicUsers: (filters?: DiscoverFilters, page?: number, limit?: number) =>
    discoverService.browsePublicUsers(filters, page, limit),
  discoverUsers: (currentUser: User, filters?: DiscoverFilters, page?: number, limit?: number) =>
    discoverService.discoverUsers(currentUser, filters, page, limit),
  getSuggestions: (currentUser: User, limit?: number) =>
    discoverService.getSuggestions(currentUser, limit),
  performAction: (action: DiscoverAction) =>
    discoverService.performAction(action),
  saveUser: (userId: string) =>
    discoverService.saveUser(userId),
  unsaveUser: (userId: string) =>
    discoverService.unsaveUser(userId),
  getSavedUsers: () =>
    discoverService.getSavedUsers(),
  reportUser: (userId: string, reason: string, details?: string) =>
    discoverService.reportUser(userId, reason, details),
  getDiscoverStats: () =>
    discoverService.getDiscoverStats(),
  sendMatchInvitation: (targetUserId: string, message?: string) =>
    discoverService.sendMatchInvitation(targetUserId, message),
  getMatchInvitations: () =>
    discoverService.getMatchInvitations(),
  respondToInvitation: (invitationId: string, response: 'accepted' | 'declined') =>
    discoverService.respondToInvitation(invitationId, response)
};

export default discoverService;
